<template>
	<v-card flat tile class="pa-3">
		<v-row class="ma-0 mt-4">
			<v-col>
				<v-row>
					<v-tooltip right>
						<template  v-slot:activator="{ on }">
						<v-switch v-on="on" color="green" v-model="project.active" class="ml-3 mr-3"></v-switch>
						</template>
						{{project.live ? 'Deactivate' : 'Activate'}} project
					</v-tooltip>
					<v-text-field v-model="project.clientName" label="Client Project Name"></v-text-field>	
				</v-row>
				<v-text-field v-model="project.participantName" label="Respondent Project Name"></v-text-field>	
				<v-text-field v-model="project.internalName" label="Internal Project Name"></v-text-field> 
				<v-text-field type="number" v-model="project.appointmentDuration"  label="Appointment Duration (minutes)" hint="Defaults to 60 minutes"></v-text-field>
				<v-select
					label="Status"
					v-model="project.status"
					:items="[ {text: 'Open', value: 'open' },{text: 'Closed', value: 'closed'}, {text: 'Paused', value: 'paused'}, {text: 'Archived', value: 'archived'} ]"
					:append-icon="selectIcon('status')"
					@click:append="project.status = false"
				></v-select>
				<v-select
					label="Methodology"
					v-model="project.methodology"
					:items="[{text: 'Face to face', value: 'face-to-face' },{text: 'Video', value: 'video'}, {text: 'Phone', value: 'phone'}]"
					:append-icon="selectIcon('methodology')"
					@click:append="project.methodology = false"
				></v-select>
				<v-select
					label="Client"
					v-model="project.client"
					:items="organisations"
					item-value="id"
					item-text="name"
					:prepend-icon="dialogIcon('client')"
					:append-icon="selectIcon('client')"
					@click:append="project.client = false"
					@click:prepend="clientDialog = 1"
					v-on:mousedown="fetch('organisations')"
				></v-select>
				<v-dialog v-model="clientDialog">
					<v-sheet v-if="projectClient"> <EditOrganisation :organisation="projectClient" :flat="true"></EditOrganisation>
					</v-sheet>
					<v-sheet v-else>
						<NewOrganisationForm v-on:created="setValue"></NewOrganisationForm>
					</v-sheet>
				</v-dialog>
				<v-autocomplete
					multiple
					label="Client Contacts"
					v-model="clientContacts"
					:items="clients"
					item-value="id"
					item-text="displayName"
					prepend-icon="mdi-plus-circle"
					@click:prepend="showCreateUser('client')"
					v-on:mousedown="fetch('clients')"
				></v-autocomplete>
				<p class="text-right">
					<v-dialog v-model="permissionsDialog" width="690px">
						<template v-slot:activator="{ on }">
							<v-btn color="accent" v-on="on">Manage Client Permissions</v-btn>
						</template>
						<PermissionManager objectType="projects" :object="project" :permissions="permissions" v-on:complete="permissionsDialog = false"></PermissionManager>
					</v-dialog>
				</p>
				<v-select
					multiple
					label="Project Managers"
					v-model="projectManagers"
					:items="admins"
					item-value="id"
					item-text="displayName"
					prepend-icon="mdi-plus-circle"
					@click:prepend="showCreateUser('admin')"
					v-on:mousedown="fetch('admins')"
				></v-select>
				<v-select
					class=""
					label="Screener"
					v-model="project.screener"
					:items="surveys"
					item-value="id"
					item-text="name"
					:append-icon="selectIcon('screener')"
					:prepend-icon="dialogIcon('screener')"
					@click:append="project.screener = false"
					@click:prepend="screenerDialog = true"
					v-on:mousedown="fetch('surveys')"
				></v-select>
				<v-dialog v-model="screenerDialog">
					<v-sheet v-if="projectScreener">
						<EditSurvey :survey="projectScreener" :flat="true"></EditSurvey>
					</v-sheet>
					<v-sheet v-else>
						<NewSurveyForm v-on:created="setValue"></NewSurveyForm>
					</v-sheet>
				</v-dialog>
				<v-row>
					<v-col>
						<small class="v-label v-label--active theme--light" style="font-size: 13px">Rescreening questions</small>
						<div v-for="(question, i) in rescreeningQuestions" :key="i">
							<v-text-field v-model="rescreeningQuestions[i]" :prefix="`Q${i+1}: `"></v-text-field>
						</div>
						<p class="text-right"><v-btn color="accent" @click="rescreeningQuestions.push('')">New Rescreening Question</v-btn></p>
					</v-col>
				</v-row>
				<v-select
					label="Instructions"
					v-model="project.instruction"
					:items="instructions"
					item-value="id"
					item-text="name"
					:append-icon="selectIcon('instruction')"
					:prepend-icon="dialogIcon('instruction')"
					@click:append="project.instruction = false"
					@click:prepend="instructionDialog = true"
					v-on:mousedown="fetch('instructions')"
				></v-select>
				<v-dialog v-model="instructionDialog">
					<v-sheet v-if="projectInstruction">
						<EditInstruction :instruction="projectInstruction"></EditInstruction>
					</v-sheet>
					<v-sheet v-else>
						<NewInstructionForm v-on:created="setValue"></NewInstructionForm>
					</v-sheet>
				</v-dialog>
				<v-select
					label="Locations"
					v-model="project.location"
					:items="locations"
					item-value="id"
					item-text="name"
					:append-icon="selectIcon('location')"
					:prepend-icon="dialogIcon('location')"
					@click:append="project.location = false"
					@click:prepend="locationDialog = true"
					v-on:mousedown="fetch('locations')"
				></v-select>
				<v-dialog v-model="locationDialog">
					<v-sheet v-if="projectLocation">
						<EditLocation :location="projectLocation"></EditLocation>
					</v-sheet>
					<v-sheet v-else>
						<NewLocationForm v-on:created="setValue"></NewLocationForm>
					</v-sheet>
				</v-dialog>
			</v-col>
		</v-row>
		<v-row class="ma-0 mt-0 mb-2">
			<v-col>
				<v-combobox
				v-model="project.quotas"
				v-on:change="removeSpaces('quotas')"
				:items="[ ]"
				label="Project Quotas"
				multiple
				chips
				></v-combobox>
			</v-col>
		</v-row>			
		<v-row class="ma-4">

			<v-switch
			v-model="project.includeCalling"
			label="Include calling?" 
			color="green"
			:value="true"
			hide-details
			></v-switch>
			<v-switch
			v-model="project.includeVideoCalling"
			label="Include video calling?" 
			color="green"
			:value="true"
			class="ml-3"
			hide-details
			></v-switch>
			<v-switch
			v-model="project.hideContactDetails"
			label="Hide Contact details?" 
			color="green"
			:value="true"
			hide-details
			class="ml-3"
			></v-switch>
		</v-row>
		<v-row>
			<v-col class="ma-3">
				<v-combobox
				v-model="project.additionalFields"
				v-on:change="removeSpaces('additionalFields')"
				:items="[ ]"
				label="Additional appointment fields"
				multiple
				chips
				></v-combobox>
			</v-col>
		</v-row>

		<p class="text-right">
			<v-btn
				color="green"
				dark
				class="mr-4"
				@click="$emit('save')"
				>Save
			</v-btn>			
		</p>
		<v-dialog v-model="userDialog">
			<v-card>
				<NewUserForm v-if="userType" :role="userType" v-on:created="setUserValue"></NewUserForm>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script type="text/javascript">
import Vuex from 'vuex'
	export default{
		name: "EditProjectInfo", 
		props: {
			project: {type: Object },
		}, 
		components:{
			NewUserForm: () => import("../../components/forms/NewUserForm"),
			EditInstruction: () => import("../../components/edits/EditInstruction"),
			NewInstructionForm: () => import("../../components/forms/NewInstructionForm"),
			EditLocation: () => import("../../components/edits/EditLocation"),
			NewLocationForm: () => import("../../components/forms/NewLocationForm"),
			NewSurveyForm: () => import("../../components/forms/NewSurveyForm"),
			EditSurvey: () => import('../edits/EditSurvey'),
			NewOrganisationForm: () => import("../../components/forms/NewOrganisationForm"),
			EditOrganisation: () => import('../edits/EditOrganisation'),
			PermissionManager: () => import('../tools/PermissionManager.vue'),

		},
		data: () =>{
			return {
				clientContacts: [],
				projectManagers: [],
				userType: false,
				userDialog: false,
				screenerDialog: false,
				instructionDialog: false,
				locationDialog: false,
				clientDialog: false,
				permissionsDialog: false,
				permissions: ['moderate', 'view-invoices', 'upload', 'view-all-appointments'],
				fetched: [], 
				rescreeningQuestions: []
			}
		}, 
		computed: {    
			...Vuex.mapState({
				view: state => state.auth.view,
				admins: state => state[state.auth.view].admins,
				clients: state => state[state.auth.view].clients,
				organisations: state => state[state.auth.view].organisations,
				instructions: state => state[state.auth.view].instructions,
				locations: state => state[state.auth.view].locations,
				surveys: state => state[state.auth.view].surveys,
			}),
			inviteInfo: function(){
				return this.project.inviteInfo ? this.project.inviteInfo : ['',''] 
			},		
			projectScreener() {
				const self = this;
				return self.surveys.find( s => s.id == self.project.screener)
			},
			projectInstruction() {
				const self = this;
				return self.instructions.find( i => i.id == self.project.instruction)
			},
			projectClient() {
				const self = this;
				return self.organisations.find( i => i.id == self.project.client)
			},
			projectLocation() {
				const self = this;
				let location = self.locations.find( loc => loc.id == self.project.location)
				return location
			},
			dateKeyOptions: function(){
				let keys = [ 'start', 'end' ]
				if( this.project.dates){
					keys = keys.concat( Object.keys(this.project.dates)) 
				}
				return keys
			}
		},
		methods: {
			removeSpaces(key){
				for( var i in this.project[key] ){
					this.project[key][i] = this.project[key][i].split(" ").join("-");
				}
			},
			showCreateUser(type){
				this.userType = type;
				this.userDialog = true;
			},
			setUserValue(id, role){
				this.project.users[id] = role;
				this.userDialog = false;

				this.userType = false;
				if( role == 'admin'){
					this.projectManagers.push(id)
				}
				if( role == 'client'){
					this.clientContacts.push(id)
				}
			},
			setValue(value, key){
				if( key == 'survey' ){ key = "screener"; }
				if( key == 'organisation' ){ key = "client"; }
				this.project[key] = value;
				this[key + 'Dialog'] = false;
			},
			dialogIcon(key){
				if( this.project[key] ){
					return "mdi-pencil-circle"
				}
				else{
					return "mdi-plus-circle"
				}
			},
			selectIcon(key){
				if( this.project[key] ){
					return "mdi-close"
				}
				else{
					return "mdi-chevron-down"
				}
			},
			fetch(type){
				if( this.fetched.indexOf(type) < 0 ){				
					this.fetched.push(type)
					this.$store.dispatch("fetch", type )
				}
			}
		},
		watch: {
			projectManagers(){
				const self = this;
				let users = {...self.project.users};
				Object.keys(users).forEach( key => {
					if( users[key] == "admin" ){ delete users[key] }
				})
				if( self.projectManagers ){				
					self.projectManagers.forEach( id => {
						users[id] = 'admin'
					})
				}
				self.project.users = users;
			},
			clientContacts(newValue, oldValue){
				const self = this;
				let users = {...self.project.users};
				oldValue.forEach( key => {
					if( newValue.indexOf(key) < 0 ){
						delete users[key];
						if( self.project.permissions ){
							delete self.project.permissions[key]
						}
					}
				})
				newValue.forEach( key => {
					if( oldValue.indexOf(key) < 0 ){
						users[key] = 'client';
						if( !self.project.permissions ){
							self.project.permissions = {};
						}
						if( !self.project.permissions[key] ){
							var permissions = {}
							self.permissions.forEach( key => permissions[key] = true );
							self.project.permissions[key] = permissions;
						}
					}
				})								
				self.project.users = users;
			}, 
			rescreeningQuestions(){
				this.project.rescreeningQuestions = this.rescreeningQuestions;
			}
		},
		created(){
			var self = this;
			if( self.project.users ){
				self.clientContacts = Object.keys(self.project.users).filter( a => self.project.users[a] == 'client');
				self.projectManagers = Object.keys(self.project.users).filter( a => self.project.users[a] == 'admin');
			}
			if( self.project.rescreeningQuestions){
				self.rescreeningQuestions = self.project.rescreeningQuestions
			}
		}

	}
</script>